import { Password } from '@src/components/Password';
import { PasswordRules } from '@src/pages/Auth/components';
import { validatePassword } from '@src/utilities/passwordValidation';
import { Variant } from 'antd/es/config-provider';
import clsx from 'clsx';
import { memo, useEffect, useState } from 'react';

import { FormController } from '../FormController';
import { FormProps } from '../types';

type FormPasswordProps = FormProps & {
  confirm?: boolean;
  referenceField?: string;
  variant?: Variant;
  showRules?: boolean;
  className?: string;
  watch?: (field: string) => string;
};

const MemoizedPasswordRules = memo(PasswordRules);

export function FormPassword({
  t,
  control,
  keyName,
  name,
  placeholder,
  error,
  confirm,
  referenceField,
  variant,
  showRules = false,
  className,
  watch,
}: FormPasswordProps) {
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (showRules) return;
    setIsFocused(false);
  }, [showRules]);

  return (
    <div
      className={clsx(
        '[&_.ant-form-item-required]:!text-wl-on-background',
        '[&_.ant-input-outlined]:!bg-wl-surface [&_.ant-input-outlined]:text-wl-on-background',
        '[&_.ant-input]:placeholder-gray-400 '
      )}
    >
      {control && (
        <FormController
          control={control}
          name={keyName || name}
          render={({ field }) => (
            <div className='space-y-2'>
              <Password
                {...field}
                placeholder={placeholder ?? ''}
                variant={variant}
                className={className}
                error={
                  error
                    ? t(error?.message?.toString() ?? 'errors.unknown')
                    : undefined
                }
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
              />
              <div
                className={clsx(
                  'transition-all duration-500 ease-in-out overflow-hidden',
                  showRules && isFocused
                    ? 'opacity-100 max-h-48 mt-2'
                    : 'opacity-0 max-h-0'
                )}
                aria-hidden={!(showRules && isFocused)}
                aria-live={showRules && isFocused ? 'polite' : undefined}
                role='region'
              >
                <MemoizedPasswordRules t={t} password={field.value} />
              </div>
            </div>
          )}
          rules={{
            required: true,
            validate: (value: string) => {
              if (confirm && referenceField && watch) {
                const referenceValue = watch(referenceField);
                if (referenceValue !== value)
                  return t('errors.passwordMismatch');
              } else {
                if (!validatePassword(value)) return false;
              }
              return true;
            },
          }}
        />
      )}
      {error && (
        <span className='text-red-500'>
          {t(error.message?.toString() ?? 'errors.unknown')}
        </span>
      )}
    </div>
  );
}
